import { useRouter } from "@/_router";
import { useTranslation } from "react-i18next";
import { useAppStore } from "@/controller";
import { useEffect, useMemo, useState, useRef, useCallback, memo } from "react";
import { useShallow } from "zustand/react/shallow";
import {
  CopyIcon,
  DownloadIcon,
  SparklesIcon,
  TargetIcon,
  PencilIcon,
  MoreHorizontalIcon,
  TrashIcon,
  UsersIcon,
  MusicIcon,
  FileTextIcon,
  CardIcon,
  HelpCircleIcon,
  PlayIcon,
  PauseIcon,
  CalendarIcon,
  ClockIcon,
  TranslateIcon,
  SearchIcon,
  TagIcon,
  PlusIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  LockIcon,
} from "@/components/Icons";
import { Tag, Transcript } from "@/types";
import { getTranscriptDirection, type ContentDirection } from "@/text-direction";
import Container from "@/components/Container";
import { useUISlice } from "@/models/useUISlice";
import { formatDuration } from "@/core";
import { contactUs } from "@/product-links";
import { useAudioPlayer } from "@/models/useAudioPlayer";
import {
  getRemainingMinutes,
  getTranscribedPercent,
  getTranscribedSeconds,
  getTranscriptFailureReason,
  isFreeDraft,
} from "@/transcript-progress";
import { recordTranscriptCreated } from "@/models/feature-adoption";
import { takeImportWaits } from "@/import-timing";
import MenuItem from "@/components/MenuItem";
import BackgroundAmbience from "@/components/BackgroundAmbience";
import PageHeader from "@/components/PageHeader";
import DropdownMenu from "@/components/DropdownMenu";
import CircularProgress from "@/components/CircularProgress";
import BottomActionBar from "@/components/BottomActionBar";
import AlertWidgetPaywall from "@/components/AlertWidgetPaywall";
import { logClientEvent } from "@/telemetry";
import TranscriptRatingWidget from "@/components/TranscriptRatingWidget";
import { TranscriptNativeCapabilities } from "@/components/transcript/TranscriptPlatformCapabilities";
import { actions, runtime } from "@/controller";
import { useAuth } from "@/models/useAuth";
import type { TFunction } from "i18next";
import toast from "react-hot-toast";
import {
  downloadTranscriptAudio,
  exportTranscript,
  transcriptPlainText,
} from "@/transcript-export";
import { checkoutTranscriptCompletionKey } from "@/lifecycle";
import {
  isUploadEntry,
  UPLOAD_ENTRY,
} from "@/onboarding-interactions";

// ─── Helpers ────────────────────────────────────────────────

const getSpeakerLabel = (
  speaker: string | undefined,
  t: (key: string, opts?: Record<string, unknown>) => string,
) => {
  if (!speaker) return null;
  return isNaN(Number(speaker))
    ? speaker
    : t("transcript.speakerNumber", { number: Number(speaker) + 1 });
};

const formatDateTime = (dateString: string) => {
  const d = new Date(dateString);
  const date = d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
  const time = d.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `${date} · ${time}`;
};

const formatDurationHuman = (seconds: number): string => {
  const total = Math.floor(seconds);
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;

  if (h > 0) return `${h}h ${m}m ${s}s`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
};

const requestAddSpeakers = async (transcriptId: string) => {
  const result = await actions.transcript.addSpeaker(transcriptId);
  if ("error" in result && result.error === "needsPremium") {
    useUISlice.getState().setPaywallVisible({
      utmSource: "transcript --> add speaker",
    });
  }
};

const promptRenameTranscript = (transcriptId: string, t: TFunction) => {
  const transcript = useAppStore
    .getState()
    .transcripts.find((item) => item.id === transcriptId);
  if (!transcript) return;
  useUISlice.getState().setAlertPrompt({
    title: t("actions.renameTranscriptTitle"),
    defaultValue: transcript.fileName,
    placeholder: t("actions.renameTranscriptPlaceholder"),
    button: {
      text: t("actions.renameTranscriptButton"),
      onPress: async (text) => {
        const fileName = text.trim();
        if (!fileName) return;
        const result = await actions.transcript.rename(transcriptId, fileName);
        if ("error" in result) return;
        toast.success(t("actions.renameTranscriptSuccess"));
        useUISlice.getState().setAlertPrompt(undefined);
      },
    },
  });
};

const promptEditSegment = (
  transcriptId: string,
  segment: Transcript["segments"][number],
  t: TFunction,
) => {
  useUISlice.getState().setAlertPrompt({
    title: t("actions.editTranscript"),
    defaultValue: segment.text,
    placeholder: t("actions.transcript"),
    multiLine: true,
    hideCancel: true,
    button: {
      text: t("actions.save"),
      onPress: async (text) => {
        const result = await actions.transcript.editSegment(transcriptId, {
          ...segment,
          text: text.trim(),
        });
        if (!("error" in result)) {
          useUISlice.getState().setAlertPrompt(undefined);
        }
      },
    },
  });
};

const promptRenameSpeaker = (
  transcriptId: string,
  previousSpeaker: string,
  t: TFunction,
) => {
  useUISlice.getState().setAlertPrompt({
    title: t("actions.renameSpeaker"),
    defaultValue: isNaN(Number(previousSpeaker))
      ? previousSpeaker
      : t("actions.speaker") + ` ${Number(previousSpeaker) + 1}`,
    placeholder: t("actions.speaker"),
    button: {
      text: t("actions.save"),
      onPress: async (text) => {
        const result = await actions.transcript.renameSpeaker(
          transcriptId,
          previousSpeaker,
          text.trim(),
        );
        if (!("error" in result)) {
          useUISlice.getState().setAlertPrompt(undefined);
        }
      },
    },
  });
};

const promptDeleteTranscript = (
  transcriptId: string,
  t: TFunction,
  onDeleted: () => void,
) => {
  useUISlice.getState().setAlertModal({
    title: t("actions.deleteTranscriptTitle"),
    subtitle: t("actions.deleteTranscriptSubtitle"),
    cancelText: t("actions.deleteTranscriptCancelButton"),
    button: {
      text: t("actions.deleteTranscriptDeleteButton"),
      destructive: true,
      onPress: async () => {
        const result = await actions.transcript.delete(transcriptId);
        if ("error" in result) return;
        toast.success(t("actions.deleteTranscriptSuccess"));
        useUISlice.getState().setAlertModal(undefined);
        onDeleted();
      },
    },
  });
};

const copyTranscript = async (transcript: Transcript, t: TFunction) => {
  await runtime.clipboard.write(transcriptPlainText(transcript));
  toast.success(t("actions.copySuccess"));
};

const openTranscriptExport = (transcript: Transcript) => {
  useUISlice.getState().setAlertExportTranscriptOptions({
    hasSpeakers: transcript.segments.some((segment) => segment.speaker),
    onExport: (options) => exportTranscript(transcript, options),
  });
};

const getTotalDuration = (segments?: Transcript["segments"]) => {
  if (!segments?.length) return null;
  const maxEnd = Math.max(...segments.map((s) => s.end));
  return maxEnd > 0 ? formatDurationHuman(maxEnd) : null;
};

const guestHandoffSearch = (transcriptId: string): string => {
  const search = new URLSearchParams(window.location.search);
  search.set("id", transcriptId);
  search.set("source", "website");
  return `?${search.toString()}`;
};

// ─── Segment Row ────────────────────────────────────────────

const SegmentRow = memo(
  ({
    segment,
    transcriptId,
    hasSpeakers,
    isActive,
    isPlaying,
    isAudioFile,
    hasAudio,
    onPlaySegment,
    onPauseAudio,
    highlightTerm,
    direction,
  }: {
    segment: Transcript["segments"][number];
    transcriptId: string;
    hasSpeakers: boolean;
    isActive: boolean;
    isPlaying: boolean;
    isAudioFile: boolean;
    hasAudio: boolean;
    onPlaySegment: (startTime: number) => void;
    onPauseAudio: () => void;
    highlightTerm?: string;
    direction: ContentDirection;
  }) => {
    const { t } = useTranslation();
    const speakerLabel = getSpeakerLabel(segment.speaker, t);

    const handleSpeakerTap = () => {
      if (segment.speaker) {
        promptRenameSpeaker(transcriptId, segment.speaker, t);
      } else {
        void requestAddSpeakers(transcriptId);
      }
    };

    const handleEditTap = () => {
      promptEditSegment(transcriptId, segment, t);
    };

    const handlePlayPause = () => {
      if (isActive && isPlaying) {
        onPauseAudio();
      } else {
        onPlaySegment(segment.start);
      }
    };

    const renderText = () => {
      if (!highlightTerm || !highlightTerm.trim()) return segment.text;

      const escapedTerm = highlightTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const parts = segment.text.split(new RegExp(`(${escapedTerm})`, "gi"));

      return parts.map((part, i) =>
        part.toLowerCase() === highlightTerm.toLowerCase() ? (
          <span
            key={i}
            className="bg-yellow-400/40 text-[var(--text-primary)] rounded px-0.5"
          >
            {part}
          </span>
        ) : (
          part
        ),
      );
    };

    return (
      <div
        id={`segment-${segment.start}`}
        className={`py-4 first:pt-0 transition-all duration-200 rounded-2xl ${
          isActive ? "-mx-3 px-3" : ""
        }`}
        style={
          isActive
            ? {
                backgroundColor: "var(--highlight-bg)",
                boxShadow: "var(--glass-edge-soft), var(--lift-sm)",
              }
            : undefined
        }
      >
        {/* Top line: Speaker, Play/Pause, Timestamp ... Pen */}
        <div className="flex items-center gap-2.5 mb-2">
          {/* Speaker (only when audio file exists) */}
          {hasAudio && (
            <button
              onClick={handleSpeakerTap}
              data-action-id="transcript.segment.speaker"
              className="active:opacity-50 transition-opacity"
            >
              {speakerLabel ? (
                <span className="text-[14px] font-bold text-[var(--text-primary)] tracking-tight">
                  {speakerLabel}
                </span>
              ) : hasSpeakers ? (
                <span className="text-[13px] font-medium text-[var(--text-muted)] italic">
                  {t("transcript.unknownSpeaker")}
                </span>
              ) : (
                <span className="text-[12px] font-medium text-[var(--text-muted)] flex items-center gap-1">
                  <UsersIcon size={12} />
                  {t("transcript.addSpeaker")}
                </span>
              )}
            </button>
          )}

          {/* Play/Pause button (only when audio file exists) */}
          {hasAudio && (
            <button
              onClick={handlePlayPause}
              data-action-id="transcript.segment.playback"
              className="icon-btn shrink-0 w-9 h-9"
            >
              {isActive && isPlaying ? (
                <PauseIcon size={12} className="text-[var(--text-primary)]" />
              ) : (
                <PlayIcon
                  size={12}
                  className="text-[var(--text-primary)] ml-0.5"
                />
              )}
            </button>
          )}

          {/* Timestamp (only for audio transcripts) */}
          {isAudioFile && (
            <span
              dir="ltr"
              className="text-[12px] text-[var(--text-muted)] tabular-nums font-medium"
            >
              {formatDuration(segment.start)}
            </span>
          )}

          {/* Spacer */}
          <div className="flex-1" />

          {/* Edit button */}
          <button
            onClick={handleEditTap}
            data-action-id="transcript.segment.edit"
            className="icon-btn icon-btn-quiet w-9 h-9 text-[var(--text-muted)] shrink-0"
            aria-label="Edit segment"
          >
            <PencilIcon size={15} />
          </button>
        </div>

        {/* Segment Text — clickable to play only when audio file exists */}
        <p
          dir={direction}
          onClick={hasAudio ? () => onPlaySegment(segment.start) : undefined}
          data-action-id="transcript.segment.playback"
          className={`text-[17px] leading-[1.7] text-[var(--text-primary)] transition-colors duration-200 whitespace-pre-line ${
            hasAudio ? "cursor-pointer active:opacity-60" : ""
          }`}
        >
          {renderText()}
        </p>
      </div>
    );
  },
);

// ─── Loading Skeleton ───────────────────────────────────────

const Skeleton = () => (
  <div className="pt-4 space-y-6 animate-pulse">
    <div className="space-y-3">
      <div className="h-7 w-3/4 rounded-xl bg-[var(--fill-2)]" />
      <div className="h-4 w-2/5 rounded-lg bg-[var(--fill-2)]" />
    </div>
    <div className="space-y-6 mt-6">
      {[1, 2, 3, 4, 5].map((i) => (
        <div key={i} className="space-y-2">
          <div className="flex justify-between">
            <div className="h-3 w-16 rounded bg-[var(--fill-2)]" />
            <div className="h-3 w-10 rounded bg-[var(--fill-2)]" />
          </div>
          <div className="h-4 w-full rounded bg-[var(--fill-2)]" />
          <div className="h-4 w-5/6 rounded bg-[var(--fill-2)]" />
        </div>
      ))}
    </div>
  </div>
);

// ─── Pending Transcription State ────────────────────────────

// Phased waiting experience calibrated to the real duration distribution
// (~90% of jobs finish under 90s, p95 ~5min, server retries can take longer):
//  fast (0-90s): brisk determinate bar — most users only ever see this
//  slow (90s-5min): indeterminate, "taking a bit longer" — p95 tail, nothing wrong
//  long (>5min): "big job, feel free to leave" — releases the user; polling
//    keeps listening in the background for up to 3h
//  retrying (server statusDetail): an attempt hit a snag, honest retry copy
type PendingPhase = "fast" | "slow" | "long" | "retrying";

const PendingState = ({
  statusDetail,
  uploadEntry,
}: {
  statusDetail?: string;
  uploadEntry: boolean;
}) => {
  const { t } = useTranslation();
  const [progress, setProgress] = useState(0);
  const [elapsedMs, setElapsedMs] = useState(0);

  useEffect(() => {
    const startedAt = Date.now();
    const intervalTime = 500;
    // Fast-fill psychology: reach ~90% around 60s so the common case feels
    // quick; the bar is only shown during the fast phase, so it never sits
    // frozen at 98% lying about a long job.
    const increment = 90 / (60000 / intervalTime);

    const timer = setInterval(() => {
      setElapsedMs(Date.now() - startedAt);
      setProgress((prev) => Math.min(prev + increment, 98));
    }, intervalTime);

    return () => clearInterval(timer);
  }, []);

  const phase: PendingPhase =
    statusDetail === "retrying" || statusDetail === "retry_requested"
      ? "retrying"
      : elapsedMs >= 5 * 60 * 1000
        ? "long"
        : elapsedMs >= 90 * 1000
          ? "slow"
          : "fast";

  const title =
    phase === "retrying"
      ? t("transcript.pending.retryingTitle")
      : phase === "long"
        ? t("transcript.pending.longTitle")
        : phase === "slow"
          ? t("transcript.pending.slowTitle")
          : t("transcript.pending.transcribing");
  // An ad visitor lives in an in-app browser: once it closes there is no
  // way back to this page, so they are never told they can leave.
  const subtitle =
    phase === "retrying"
      ? t("transcript.pending.retryingSubtitle")
      : uploadEntry
        ? t(
            phase === "fast"
              ? "transcript.pending.entryPreview"
              : "transcript.pending.entryKeepOpen",
          )
        : phase === "long"
          ? t("transcript.pending.longSubtitle")
          : t("transcript.pending.leaveScreen");

  return (
    <div className="flex flex-col items-center justify-center text-center pt-24 pb-16">
      {phase === "fast" ? (
        <CircularProgress progress={progress} size="lg" />
      ) : (
        // Indeterminate past the fast phase: a percentage we can't back up
        // is a promise we'd break — a spinning arc stays honest.
        <CircularProgress progress={0} size="lg" indeterminate />
      )}

      <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2 tracking-tight">
        {title}
      </h3>
      <p className="text-[14px] text-[var(--text-tertiary)] leading-relaxed max-w-[260px]">
        {subtitle}
      </p>
    </div>
  );
};

// ─── Failed Transcription State ─────────────────────────────

const FailedState = ({
  transcriptId,
  transcriptionError,
}: {
  transcriptId: string;
  transcriptionError?: string;
}) => {
  const { t } = useTranslation();
  const reason = getTranscriptFailureReason(transcriptionError);
  const router = useRouter();
  const isSubscribed = useAppStore((state) => state.isSubscribed);

  return (
    <div className="flex flex-col items-center justify-center text-center pt-24 pb-16 px-6">
      {/* Error icon */}
      <div className="w-20 h-20 rounded-full bg-[var(--destructive-bg)] flex items-center justify-center mb-6">
        <svg
          width={32}
          height={32}
          viewBox="0 0 24 24"
          fill="none"
          stroke="var(--destructive)"
          strokeWidth="1.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="15" y1="9" x2="9" y2="15" />
          <line x1="9" y1="9" x2="15" y2="15" />
        </svg>
      </div>

      <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2 tracking-tight">
        {reason ? t(`transcript.failed.reasons.${reason}.title`) : t("transcript.failed.title")}
      </h3>
      <p className="text-[14px] text-[var(--text-tertiary)] leading-relaxed max-w-[280px] mb-8">
        {reason ? t(`transcript.failed.reasons.${reason}.subtitle`) : t("transcript.failed.subtitle")}
      </p>

      {/* Actions */}
      <div className="w-full max-w-[300px] space-y-3">
        {isSubscribed && (
          <button
            data-action-id="transcript.failed.support"
            onClick={() => {
              contactUs(
                encodeURIComponent("Transcription failed"),
                encodeURIComponent(
                  `Hello, my transcription failed. Transcript ID: ${transcriptId}`,
                ),
              );
            }}
            className="btn btn-primary w-full text-[15px] active:scale-[0.97] transition-all"
          >
            {t("transcript.failed.contactSupport")}
          </button>
        )}

        <button
          data-action-id="transcript.failed.delete"
          onClick={() => {
            promptDeleteTranscript(transcriptId, t, () =>
              router.replace("/home"),
            );
          }}
          className="btn btn-quiet w-full text-[14px] text-[var(--destructive)]"
        >
          {t("transcript.failed.delete")}
        </button>
      </div>
    </div>
  );
};

// ─── Main Page ──────────────────────────────────────────────

export default function TranscriptPage() {
  const { t } = useTranslation();
  const router = useRouter();
  const replaceRoute = router.replace;
  const transcriptId = router.query?.id;
  const search = new URLSearchParams(window.location.search);
  const uploadEntry = isUploadEntry(
    window.location.search,
  );
  const guestToken = new URLSearchParams(window.location.hash.slice(1)).get(
    "guestToken",
  );
  const isWebsiteHandoff = search.get("source") === "website" || !!guestToken;
  const searchTimestamp = router.query?.t ? Number(router.query.t) : undefined;
  const searchQuery = router.query?.q as string | undefined;
  const lastScrolledTimestamp = useRef<number | undefined>(undefined);
  const transcript = useAppStore(
    useCallback(
      (state) => state.transcripts.find((tr) => tr.id === transcriptId),
      [transcriptId],
    ),
  );
  const { allTags, isSubscribed } = useAppStore(
    useShallow((state) => ({
      allTags: state.userData?.allTags,
      isSubscribed: state.isSubscribed,
    })),
  );

  useEffect(() => {
    if (!uploadEntry || !isSubscribed) return;
    const nextSearch = new URLSearchParams(window.location.search);
    nextSearch.delete("entry");
    replaceRoute({ pathname: "/transcript", search: `?${nextSearch}` });
  }, [isSubscribed, uploadEntry, replaceRoute]);

  const tagsIds = transcript?.tagsIds;
  const tags = useMemo(() => {
    if (!transcriptId || !tagsIds?.length || !allTags?.length) return [];
    return tagsIds
      .map((tagName) => allTags.find((tag) => tag.name === tagName))
      .filter((tag): tag is Tag => Boolean(tag));
  }, [transcriptId, tagsIds, allTags]);

  const [isScrolled, setIsScrolled] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showBottomMenu, setShowBottomMenu] = useState(false);
  const [visibleSegmentsCount, setVisibleSegmentsCount] = useState(30);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [isOfferInView, setIsOfferInView] = useState(false);
  const [partialNoticeHeight, setPartialNoticeHeight] = useState(0);
  const partialNoticeRef = useRef<HTMLButtonElement>(null);
  const [guestHandoffStatus, setGuestHandoffStatus] = useState<
    "idle" | "loading" | "failed"
  >(guestToken ? "loading" : "idle");
  const scrollRef = useRef<HTMLDivElement>(null);
  const guestOfferRef = useRef<HTMLDivElement>(null);
  const guestOfferSeenRef = useRef<string | undefined>(undefined);
  const guestViewedTranscriptRef = useRef<string | undefined>(undefined);
  const guestCopiedTranscriptRef = useRef<string | undefined>(undefined);
  const guestExportedTranscriptRef = useRef<string | undefined>(undefined);
  const guestHandoffRunRef = useRef(0);
  const checkoutCompletionNoticeRef = useRef<string | undefined>(undefined);

  // Keep the import sheet covering Home until this destination has committed.
  // Dismissing it from the upload callback can expose Home for one paint while
  // the router is still switching pages.
  useEffect(() => {
    const ui = useUISlice.getState();
    if (ui.alertWidgetCreateTranscriptFromFileImport) {
      ui.setAlertCreateWidgetTranscriptFromFileImport(undefined);
    }
  }, []);

  const fileTitle = transcript?.fileName?.replace(/\.[^/.]+$/, "");
  const title = fileTitle || t("home.untitled");
  const hasSpeakers = useMemo(
    () => transcript?.segments?.some((s) => s.speaker) || false,
    [transcript?.segments],
  );
  const totalDuration = useMemo(
    () => getTotalDuration(transcript?.segments),
    [transcript?.segments],
  );
  const isPending =
    transcript?.status === "transcription_started" ||
    transcript?.status === "upload_started" ||
    transcript?.status === "upload_completed";
  const isFailed =
    transcript?.status === "transcription_failed" ||
    transcript?.status === "upload_failed";
  const hasSegments = transcript?.segments && transcript.segments.length > 0;
  const showAiTools =
    hasSegments &&
    !isPending &&
    !isFailed &&
    transcriptId &&
    !uploadEntry;
  const showPartialTranscriptUpsell =
    transcript?.isFullTranscript === false &&
    !!transcript?.originalFileUrl &&
    !isPending &&
    !isFailed;

  // Audio
  const {
    audioRef,
    isPlaying: audioIsPlaying,
    playbackRate,
    hasAudio,
    isAudioFile,
    durationSeconds,
    activeSegmentIndex,
    togglePlayPause,
    playFromTime: handlePlaySegment,
    pause: handlePauseAudio,
    toggleSpeed,
  } = useAudioPlayer(transcript);

  const showShortTranscriptUpsell =
    !isSubscribed &&
    isAudioFile &&
    transcript?.isFullTranscript === true &&
    transcript.status === "transcription_completed" &&
    !!transcript.originalFileUrl &&
    !!hasSegments;
  const showSubscriptionOffer =
    !isSubscribed && (showPartialTranscriptUpsell || showShortTranscriptUpsell);

  // A free draft says what it is up front, before the reader judges the text.
  const showDraftNotice =
    !isSubscribed && !!hasSegments && !isPending && isFreeDraft(transcript);
  // A subscriber's file made before they upgraded can be redone on Premium.
  const isPremiumRedo =
    transcript?.isFullTranscript === true && isFreeDraft(transcript);

  const showTranscribeFullAudioButton =
    isSubscribed &&
    (transcript?.isFullTranscript === false || isPremiumRedo) &&
    hasAudio &&
    !isPending &&
    !isFailed;

  // How much of the file has text. Null until media metadata lands; the
  // notice uses generic copy in that case instead of an invented percentage.
  const [transcribedPercent, remainingMinutes] = useMemo(() => {
    const progress = {
      transcribedSeconds: getTranscribedSeconds(transcript?.segments),
      mediaSeconds: durationSeconds,
    };
    return [getTranscribedPercent(progress), getRemainingMinutes(progress)];
  }, [transcript?.segments, durationSeconds]);

  // Long cut-off files only; a short file's offer is the draft card.
  const showOfferNotice =
    !isSubscribed && showPartialTranscriptUpsell && !isOfferInView;
  const partialNoticeBottom = uploadEntry ? "1rem" : "8rem";

  const runGuestHandoff = useCallback(async () => {
    if (!guestToken || !transcriptId) return;
    const runId = ++guestHandoffRunRef.current;
    const isCurrent = () => guestHandoffRunRef.current === runId;
    setGuestHandoffStatus("loading");
    logClientEvent("guest.handoffStarted", { source: "website" });

    const remoteConfig = actions.user.remoteConfig();
    const guestResult = await actions.transcript.getGuest(
      transcriptId,
      guestToken,
    );
    if (!isCurrent()) return;
    if ("error" in guestResult) {
      logClientEvent("guest.handoffFailed", {
        source: "website",
        step: "fetch",
        reason: guestResult.error,
      });
      setGuestHandoffStatus("failed");
      return;
    }

    const guestUser = useAuth.currentAuthUser(guestToken);
    if (!guestUser) {
      logClientEvent("guest.handoffFailed", {
        source: "website",
        step: "identity",
      });
      setGuestHandoffStatus("failed");
      return;
    }

    const session = await actions.session.start(guestToken);
    if ("error" in session) {
      logClientEvent("guest.handoffFailed", {
        source: "website",
        step: "identity",
        reason: session.error,
      });
      setGuestHandoffStatus("failed");
      return;
    }

    const loaded = await actions.transcript.get(guestResult.value.id);
    if (!isCurrent()) return;
    if ("error" in loaded) {
      logClientEvent("guest.handoffFailed", {
        source: "website",
        step: "load",
        reason: loaded.error,
      });
      setGuestHandoffStatus("failed");
      return;
    }

    void remoteConfig;
    void actions.user.profile();
    logClientEvent("guest.handoffCompleted", { source: "website" });
    setGuestHandoffStatus("idle");
    replaceRoute({
      pathname: "/transcript",
      search: guestHandoffSearch(loaded.value.id),
      hash: "",
    });
  }, [guestToken, replaceRoute, transcriptId]);

  useEffect(() => {
    if (!guestToken || !transcriptId) return;
    void runGuestHandoff();
    return () => {
      guestHandoffRunRef.current += 1;
    };
  }, [guestToken, runGuestHandoff, transcriptId]);

  // Fetch transcript if not loaded
  useEffect(() => {
    if (transcriptId && !transcript && !guestToken) {
      void actions.transcript.get(transcriptId);
    }
  }, [guestToken, transcript, transcriptId]);

  useEffect(() => {
    if (
      !isWebsiteHandoff ||
      !transcript ||
      guestViewedTranscriptRef.current === transcript.id
    ) {
      return;
    }
    guestViewedTranscriptRef.current = transcript.id;
    logClientEvent("guest.transcriptViewed", {
      source: "website",
      feature: transcript.type,
      step: transcript.segments.length ? "withSegments" : "empty",
    });
  }, [isWebsiteHandoff, transcript]);

  // The offer scrolled into view: the step between reading and buying.
  useEffect(() => {
    if (
      !showSubscriptionOffer ||
      !transcript ||
      guestOfferSeenRef.current === transcript.id
    ) {
      return;
    }
    const node = guestOfferRef.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (
          !entry?.isIntersecting ||
          guestOfferSeenRef.current === transcript.id
        ) {
          return;
        }
        guestOfferSeenRef.current = transcript.id;
        logClientEvent("transcript.offerViewed", {
          source: uploadEntry
            ? "entry"
            : isWebsiteHandoff
              ? "website"
              : "transcript",
          display: showPartialTranscriptUpsell ? "partial" : "accuracy",
        });
        if (isWebsiteHandoff && showPartialTranscriptUpsell) {
          logClientEvent("guest.offerViewed", { source: "transcript" });
        }
        observer.disconnect();
      },
      { threshold: 0.35 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [
    isWebsiteHandoff,
    showPartialTranscriptUpsell,
    showSubscriptionOffer,
    transcript,
    uploadEntry,
  ]);

  // The floating notice exists to point at the offer below the fold, so it
  // stands down once that offer is actually on screen — otherwise it covers
  // the control it is asking the reader to use.
  useEffect(() => {
    if (!showSubscriptionOffer) {
      setIsOfferInView(false);
      return;
    }
    const node = guestOfferRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsOfferInView(Boolean(entry?.isIntersecting)),
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [showSubscriptionOffer, transcript?.id]);

  // The notice is a full-width bar in the band the scroll-top button used to
  // own alone, and its height depends on how many lines the translated copy
  // takes — German runs to three where English takes one. Measuring is the
  // only offset that holds for every locale; a fixed one collides somewhere.
  useEffect(() => {
    const node = partialNoticeRef.current;
    if (!showOfferNotice || !node) {
      setPartialNoticeHeight(0);
      return;
    }
    const measure = () => setPartialNoticeHeight(node.offsetHeight);
    measure();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(measure);
    observer.observe(node);
    return () => observer.disconnect();
  }, [showOfferNotice, transcribedPercent]);

  // Scroll to search result
  useEffect(() => {
    if (
      searchTimestamp !== undefined &&
      hasSegments &&
      lastScrolledTimestamp.current !== searchTimestamp
    ) {
      // Find the segment index
      const segmentIndex = transcript?.segments.findIndex(
        (s) => s.start === searchTimestamp,
      );

      if (
        segmentIndex !== undefined &&
        segmentIndex !== -1 &&
        segmentIndex >= visibleSegmentsCount
      ) {
        setVisibleSegmentsCount(segmentIndex + 50);
        return;
      }

      // Find the segment that matches the timestamp
      // We rely on the ID format `segment-${start}`
      // searchTimestamp should match one of the segment.start values exactly as it comes from search index which comes from segments
      const elementId = `segment-${searchTimestamp}`;

      // Use a timeout to ensure DOM is rendered
      const timer = setTimeout(() => {
        const el = document.getElementById(elementId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth", block: "center" });
          lastScrolledTimestamp.current = searchTimestamp;
        } else {
          // Fallback: try to find the closest segment if exact match fails
          // This handles potential floating point differences or if search index is slightly off
          const segmentIds = transcript?.segments.map((s) => s.start) || [];
          if (segmentIds.length > 0) {
            const closest = segmentIds.reduce((prev, curr) =>
              Math.abs(curr - searchTimestamp) <
              Math.abs(prev - searchTimestamp)
                ? curr
                : prev,
            );
            const closestEl = document.getElementById(`segment-${closest}`);
            if (closestEl) {
              closestEl.scrollIntoView({ behavior: "smooth", block: "center" });
              lastScrolledTimestamp.current = searchTimestamp;
            }
          }
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [searchTimestamp, hasSegments, transcript, visibleSegmentsCount]);

  // Start persistent polling while transcribing (lives in the store, survives navigation)
  useEffect(() => {
    if (transcriptId && transcript) {
      actions.transcript.pollStatus(transcriptId);
    }
  }, [transcript, transcriptId]);

  useEffect(() => {
    if (transcript?.status !== "transcription_completed") return;
    recordTranscriptCreated();
  }, [transcript?.status]);

  // How long the person waited for text they just uploaded, measured to the
  // moment it is on screen: the speed that matters for converting a visit.
  useEffect(() => {
    if (
      !transcriptId ||
      !hasSegments ||
      transcript?.status !== "transcription_completed"
    ) {
      return;
    }
    const waits = takeImportWaits(transcriptId, Date.now());
    if (!waits) return;
    const source = uploadEntry ? "entry" : "home";
    const details = { source, transcriptId, sizeMb: waits.sizeMb };
    logClientEvent("import.transcriptionCompleted", {
      ...details,
      elapsedMs: waits.transcriptionMs,
    });
    logClientEvent("import.transcriptShown", {
      ...details,
      elapsedMs: waits.totalMs,
    });
  }, [transcriptId, hasSegments, transcript?.status, uploadEntry]);

  const previewViewedRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (
      isSubscribed ||
      !transcriptId ||
      !hasSegments ||
      transcript?.status !== "transcription_completed" ||
      previewViewedRef.current === transcriptId
    ) {
      return;
    }
    previewViewedRef.current = transcriptId;
    logClientEvent("transcript.previewViewed", {
      feature: transcript.type,
      source: uploadEntry ? "entry" : undefined,
    });
  }, [
    isSubscribed,
    transcriptId,
    hasSegments,
    transcript?.status,
    transcript?.type,
    uploadEntry,
  ]);

  useEffect(() => {
    if (
      !transcriptId ||
      transcript?.status !== "transcription_completed" ||
      checkoutCompletionNoticeRef.current === transcriptId
    ) {
      return;
    }

    let disposed = false;
    void runtime.storage
      .getItem(checkoutTranscriptCompletionKey(transcriptId))
      .then(async (pendingNotice) => {
        if (disposed || pendingNotice !== "true") return;
        checkoutCompletionNoticeRef.current = transcriptId;
        try {
          await runtime.storage.removeItem(
            checkoutTranscriptCompletionKey(transcriptId),
          );
        } catch {
          // The in-memory guard still prevents a duplicate in this session.
        }
        if (disposed) return;

        const title = t("actions.transcriptReadyTitle", {
          defaultValue: "Your full transcript is ready",
        });
        const ui = useUISlice.getState();
        // Never replace a sheet the buyer is still on (account sign-in or
        // the get-the-apps step after it); a toast carries the news instead.
        if (ui.alertPromptSignIn || ui.alertModal) {
          toast.success(title, { duration: 8_000 });
          return;
        }
        ui.setAlertModal({
          title,
          subtitle: t("actions.transcriptReadyDescription", {
            defaultValue:
              "The rest of your file has finished transcribing and is ready below.",
          }),
          button: {
            text: t("actions.transcriptReadyButton", {
              defaultValue: "View transcript",
            }),
            onPress: () => useUISlice.getState().setAlertModal(undefined),
          },
        });
      })
      .catch(() => {});

    return () => {
      disposed = true;
    };
  }, [transcriptId, transcript?.status, t]);

  // An ad visitor goes back to the focused import sheet, not the full Home.
  const navigateHome = useCallback(
    () =>
      replaceRoute(
        uploadEntry
          ? { pathname: "/home", search: `?entry=${UPLOAD_ENTRY}` }
          : `/home`,
      ),
    [replaceRoute, uploadEntry],
  );

  const scrollRafRef = useRef<number | null>(null);
  const handleScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const scrollTop = e.currentTarget.scrollTop;
    if (scrollRafRef.current !== null) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      setIsScrolled(scrollTop > 10);
      setShowScrollTop(scrollTop > 400);
      scrollRafRef.current = null;
    });
  }, []);

  const scrollToTop = () => {
    scrollRef.current?.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Take the reader to the offer the notice is about. scrollIntoView lands it
  // centred; the container fallback covers the case where the footer has not
  // mounted yet, which would otherwise make the notice a dead tap.
  const scrollToOffer = () => {
    if (showPartialTranscriptUpsell) {
      logClientEvent("transcript.partialProgressSelected", {
        percent: transcribedPercent,
      });
    }
    const offer = guestOfferRef.current;
    if (offer) {
      offer.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    const container = scrollRef.current;
    container?.scrollTo({ top: container.scrollHeight, behavior: "smooth" });
  };

  const selectDraftNotice = () => {
    logClientEvent("transcript.draftNoticeSelected", {
      display: showPartialTranscriptUpsell ? "partial" : "full",
    });
    if (showSubscriptionOffer) {
      scrollToOffer();
      return;
    }
    useUISlice.getState().setPaywallVisible({
      utmSource: "transcript --> draft notice",
    });
  };

  // Keep the subscription offer reachable while the reader is above it. It
  // rides on top of the bottom action bar (the screen edge, not the reading
  // area, so swipes on the text still scroll), and floats only on the upload
  // entry, which has no bar.
  const offerNotice = showOfferNotice ? (
    <button
      ref={partialNoticeRef}
      onClick={scrollToOffer}
      data-action-id="transcript.subscriptionOffer"
      style={{
        backgroundImage:
          "linear-gradient(135deg, color-mix(in srgb, var(--premium-cta-start) 38%, transparent), transparent 85%)",
        ...(uploadEntry
          ? { bottom: `calc(env(safe-area-inset-bottom, 0px) + ${partialNoticeBottom})` }
          : {}),
      }}
      className={`glass flex min-h-11 items-center gap-2.5 rounded-2xl py-2.5 pl-3 pr-2.5 text-start transition-transform active:scale-[0.98] ${
        uploadEntry
          ? "fixed left-4 right-4 z-20 md:left-1/2 md:right-auto md:w-[min(36rem,calc(100vw-3rem))] md:-translate-x-1/2"
          : "mx-auto mb-2.5 w-full max-w-[36rem]"
      }`}
    >
      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[var(--premium-cta-start)] to-[var(--premium-cta-end)]">
        <LockIcon size={14} className="text-[var(--premium-cta-text)]" />
      </span>
      <span className="flex min-w-0 flex-1 flex-col">
        {/* Wraps rather than truncates. Cut at the width a 320px phone
            leaves here, the English line lands on "Only 5% of this file
            i…" — the sentence loses the only word that matters. */}
        <span className="text-[13px] font-bold leading-tight text-[var(--text-primary)]">
          {transcribedPercent === null
            ? t("alertWidgetPaywall.footerTitle")
            : t("transcript.partialNoticeTitle", {
                percent: transcribedPercent,
              })}
        </span>
        <span className="mt-0.5 text-[11.5px] leading-tight text-[var(--text-secondary)]">
          {t("transcript.partialNoticeCta")}
        </span>
      </span>
      <ChevronDownIcon
        size={18}
        className="shrink-0 text-[var(--text-primary)]"
      />
    </button>
  ) : null;

  // Reading and copying the free transcript is the free trial; taking it out
  // as a file or into another language is what Premium sells.
  const showEntryActionPaywall = (action: "download" | "translate") => {
    if (!uploadEntry || isSubscribed) return false;
    useUISlice.getState().setPaywallVisible({
      utmSource: `upload-entry-${action}`,
    });
    return true;
  };

  // Every copy and export control goes through these two, so each logs once
  // here rather than per button (the header buttons used to log nothing).
  const copyVisibleTranscript = () => {
    if (!transcript) return false;
    logClientEvent("transcript.copied", { transcriptId: transcript.id });
    if (
      isWebsiteHandoff &&
      guestCopiedTranscriptRef.current !== transcript.id
    ) {
      guestCopiedTranscriptRef.current = transcript.id;
      logClientEvent("guest.previewCopied", { source: "transcript" });
    }
    void copyTranscript(transcript, t);
    return true;
  };

  const exportVisibleTranscript = () => {
    if (!transcript || showEntryActionPaywall("download")) return false;
    logClientEvent("transcript.exportSelected", { transcriptId: transcript.id });
    if (
      isWebsiteHandoff &&
      guestExportedTranscriptRef.current !== transcript.id
    ) {
      guestExportedTranscriptRef.current = transcript.id;
      logClientEvent("guest.previewExported", { source: "transcript" });
    }
    openTranscriptExport(transcript);
    return true;
  };

  const openTranslation = () => {
    if (!transcriptId || showEntryActionPaywall("translate")) return;
    useUISlice.getState().setAlertLanguagePicker({
      title: t("transcript.aiPanel.translateTo"),
      onSelect: (languageCode) => {
        router.push({
          pathname: "/translate",
          search: `?${new URLSearchParams({
            id: transcriptId,
            lang: languageCode,
          })}`,
        });
      },
    });
  };

  const playGuestAudio = (startTime?: number) => {
    if (isWebsiteHandoff) {
      logClientEvent("guest.audioPlayed", {
        source: startTime === undefined ? "player" : "segment",
      });
    }
    if (startTime === undefined) togglePlayPause();
    else handlePlaySegment(startTime);
  };

  if (guestHandoffStatus === "failed") {
    return (
      <Container className="fixed inset-0 app-page-background flex items-center justify-center px-6">
        <div className="glass w-full max-w-md rounded-[28px] p-7 text-center">
          <h1 className="text-[24px] font-bold tracking-tight text-[var(--text-primary)]">
            {t("transcript.guestHandoffErrorTitle")}
          </h1>
          <p className="mt-3 text-[15px] leading-6 text-[var(--text-secondary)]">
            {t("transcript.guestHandoffErrorBody")}
          </p>
          <button
            data-action-id="transcript.guestHandoff.retry"
            onClick={() => void runGuestHandoff()}
            className="mt-6 min-h-[var(--control-lg)] w-full rounded-full bg-[var(--accent)] px-5 font-semibold text-[var(--text-inverse)] active:scale-[0.98] transition-transform"
          >
            {t("transcript.guestHandoffRetry")}
          </button>
        </div>
      </Container>
    );
  }

  return (
    <Container
      reading
      className="fixed inset-0 bg-[var(--bg-primary)] text-[var(--text-primary)] flex flex-col font-sans overflow-hidden selection:bg-[var(--accent)]/20"
    >
      <style>{`
        @keyframes gentle-bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .animate-gentle-bounce {
          animation: gentle-bounce 2s infinite ease-in-out;
        }
      `}</style>
      <BackgroundAmbience />

      {/* ─── Header ─── */}
      {/* For an ad visitor this transcript is the destination, so there is no
          back — except on a failed file, where back returns them to the import
          sheet to try another. Subscribing clears the entry flag and restores
          back with the full app. */}
      <PageHeader
        isScrolled={isScrolled}
        onBack={uploadEntry && !isFailed ? undefined : navigateHome}
      >
        {/* Play/Pause control pill */}
        {hasAudio && (
          <button
            onClick={() => playGuestAudio()}
            data-action-id="transcript.playback.toggle"
            className="glass h-11 px-4 rounded-full flex items-center justify-center active:scale-90 transition-all text-[var(--text-primary)] min-w-[3rem]"
          >
            {audioIsPlaying ? (
              <PauseIcon size={15} />
            ) : (
              <PlayIcon size={15} className="ml-0.5" />
            )}
          </button>
        )}

        {/* Speed control pill */}
        {hasAudio && (
          <button
            dir="ltr"
            onClick={toggleSpeed}
            data-action-id="transcript.playback.speed"
            className="glass h-11 px-4 rounded-full flex items-center justify-center active:scale-90 transition-all text-[var(--text-primary)] text-[13px] font-semibold tabular-nums min-w-[3rem]"
          >
            {playbackRate}x
          </button>
        )}

        <button
          onClick={() => setShowMore(!showMore)}
          data-action-id="transcript.menu.toggle"
          className="icon-btn active:scale-90 transition-all text-[var(--text-primary)]"
        >
          <MoreHorizontalIcon size={20} />
        </button>
      </PageHeader>

      {/* ─── More Menu Overlay ─── */}
      <DropdownMenu open={showMore} onClose={() => setShowMore(false)}>
        <MenuItem
          actionId="transcript.rename"
          icon={<PencilIcon size={16} />}
          label={t("transcript.menu.rename")}
          onClick={() => {
            setShowMore(false);
            if (transcriptId) promptRenameTranscript(transcriptId, t);
          }}
        />

        <MenuItem
          actionId="transcript.tags.open"
          icon={<TagIcon size={16} />}
          label={t("alertTagPicker.tags", "Tags")}
          onClick={() => {
            setShowMore(false);
            if (transcriptId) {
              useUISlice.getState().setAlertTagPicker({
                transcriptIds: [transcriptId],
              });
            }
          }}
        />

        {hasSegments && (
          <MenuItem
            actionId="transcript.copy"
            icon={<CopyIcon size={16} />}
            label={t("transcript.copy")}
            onClick={() => {
              setShowMore(false);
              copyVisibleTranscript();
            }}
          />
        )}

        {hasSegments && (
          <MenuItem
            actionId="transcript.export"
            icon={<DownloadIcon size={16} />}
            label={t("transcript.download")}
            onClick={() => {
              setShowMore(false);
              exportVisibleTranscript();
            }}
          />
        )}

        {hasAudio && !hasSpeakers && hasSegments && (
          <MenuItem
            actionId="transcript.speakers.add"
            icon={<UsersIcon size={16} />}
            label={t("transcript.menu.addSpeakers")}
            onClick={() => {
              setShowMore(false);
              if (transcriptId) void requestAddSpeakers(transcriptId);
            }}
          />
        )}

        {hasAudio && (
          <MenuItem
            actionId="transcript.audio.download"
            icon={<MusicIcon size={16} />}
            label={t("transcript.menu.downloadAudio")}
            onClick={() => {
              setShowMore(false);
              if (transcript) void downloadTranscriptAudio(transcript);
            }}
          />
        )}

        <MenuItem
          actionId="transcript.delete"
          icon={<TrashIcon size={16} />}
          label={t("transcript.menu.delete")}
          destructive
          onClick={() => {
            setShowMore(false);
            if (transcriptId)
              promptDeleteTranscript(transcriptId, t, navigateHome);
          }}
        />
      </DropdownMenu>

      {/* ─── Scrollable Content ─── */}
      <main
        ref={scrollRef}
        className="flex-1 relative z-10 overflow-y-auto"
        onScroll={handleScroll}
      >
        <div
          data-testid="transcript-content"
          className="px-5 md:px-8 max-w-3xl mx-auto w-full"
        >
          {/* Loading */}
          {!transcript && <Skeleton />}

          {/* Hidden audio element */}
          {hasAudio && (
            <audio
              ref={audioRef}
              src={transcript!.originalFileUrl!}
              preload="metadata"
            />
          )}

          {/* Title & Metadata */}
          {transcript && (
            <div className="pt-4 pb-5">
              <button
                data-action-id="transcript.rename"
                onClick={() => {
                  if (transcriptId) promptRenameTranscript(transcriptId, t);
                }}
                className="text-start w-full group"
              >
                <h1
                  dir="auto"
                  className="text-[26px] font-bold text-[var(--text-primary)] leading-tight tracking-tight group-active:opacity-50 transition-opacity"
                >
                  {title}
                </h1>
              </button>

              <div className="flex items-center gap-3 mt-2.5 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <CalendarIcon
                    size={13}
                    className="text-[var(--text-muted)]"
                  />
                  <span className="text-[13px] text-[var(--text-muted)]">
                    {formatDateTime(transcript.createdAt)}
                  </span>
                </div>
                {totalDuration && isAudioFile && (
                  <div className="flex items-center gap-1.5">
                    <ClockIcon size={13} className="text-[var(--text-muted)]" />
                    <span
                      dir="ltr"
                      className="text-[13px] text-[var(--text-muted)] tabular-nums"
                    >
                      {totalDuration}
                    </span>
                  </div>
                )}
              </div>

              {/* Tags Section */}
              <div className="flex flex-wrap gap-2 mt-4">
                {tags.map((tag) => (
                  <button
                    data-action-id="transcript.tags.open"
                    key={tag.name}
                    onClick={() => {
                      if (transcriptId) {
                        useUISlice.getState().setAlertTagPicker({
                          transcriptIds: [transcriptId as string],
                        });
                      }
                    }}
                    className="glass h-9 pl-2.5 pr-3.5 rounded-full flex items-center gap-2 active:scale-95 transition-all border border-transparent hover:"
                  >
                    <div
                      className="w-2.5 h-2.5 rounded-full"
                      style={{
                        backgroundColor: tag.color,
                        boxShadow: `0 0 8px ${tag.color}60`,
                      }}
                    />
                    <span className="text-[13px] font-medium text-[var(--text-secondary)]">
                      {tag.name}
                    </span>
                  </button>
                ))}

                {/* Only show the small Plus button if there are tags. 
                  If no tags, don't show anything here because we have the main 'Tag' button below. 
                  This removes the "black tag button" the user disliked. */}
                {tags.length > 0 && (
                  <button
                    data-action-id="transcript.tags.open"
                    onClick={() => {
                      if (transcriptId) {
                        useUISlice.getState().setAlertTagPicker({
                          transcriptIds: [transcriptId as string],
                        });
                      }
                    }}
                    className="glass h-9 px-3.5 rounded-full flex items-center gap-1.5 active:scale-95 transition-all text-[var(--text-secondary)]"
                  >
                    <PlusIcon size={14} />
                  </button>
                )}
              </div>

              {/* Copy & Download buttons */}
              {hasSegments && !isPending && (
                <div className="flex items-center gap-2 mt-3">
                  <button
                    data-action-id="transcript.copy"
                    onClick={copyVisibleTranscript}
                    className="glass h-11 px-4 rounded-full flex items-center justify-center gap-2 text-[var(--text-secondary)] text-[13px] font-medium active:scale-[0.97] active:bg-[var(--glass-bg-active)] transition-all"
                  >
                    <CopyIcon size={14} />
                    {t("transcript.copy")}
                  </button>
                  <button
                    data-action-id="transcript.export"
                    onClick={exportVisibleTranscript}
                    className="glass h-11 px-4 rounded-full flex items-center justify-center gap-2 text-[var(--text-secondary)] text-[13px] font-medium active:scale-[0.97] active:bg-[var(--glass-bg-active)] transition-all"
                  >
                    <DownloadIcon size={14} />
                    {t("transcript.download")}
                  </button>
                  <button
                    data-action-id="transcript.translate.open"
                    onClick={openTranslation}
                    className="glass h-11 px-4 rounded-full flex items-center justify-center gap-2 text-[var(--text-secondary)] text-[13px] font-medium active:scale-[0.97] active:bg-[var(--glass-bg-active)] transition-all"
                  >
                    <TranslateIcon size={14} />
                    {t("transcript.translate")}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* AI tools — one horizontal scrollview of full cards so every
              function is equally visible. Summary is the first card. All
              actions are free here; the destination pages handle their own
              gating where it applies. Dictation isn't included — it doesn't
              operate on a transcript. */}
          {showAiTools && (
            <div className="mb-5">
              <div className="flex items-center gap-2 mb-2.5">
                <SparklesIcon size={14} className="text-[var(--text-muted)]" />
                <span className="text-xs font-semibold text-[var(--text-muted)] uppercase tracking-widest">
                  {t("transcript.aiTools.label")}
                </span>
              </div>

              <div className="-mx-1 px-1 flex gap-3 overflow-x-auto no-scrollbar ">
                {/* Free draft: first in the rail so it costs no height; it
                    says what the reader has and points to the offer below. */}
                {showDraftNotice && (
                  <button
                    data-action-id="transcript.draftNotice"
                    data-testid="transcript-draft-notice"
                    onClick={selectDraftNotice}
                    // Same glass and lift as its siblings; the warm wash comes
                    // from the theme's premium tokens, so dark mode follows.
                    style={{
                      backgroundImage:
                        "linear-gradient(135deg, color-mix(in srgb, var(--premium-cta-start) 42%, transparent), transparent 80%)",
                    }}
                    className="glass-tile glass-raise shrink-0 w-[200px] min-h-[135px] p-4 rounded-3xl flex flex-col items-start text-start active:scale-[0.98] active:bg-[var(--glass-bg-active)] transition-all"
                  >
                    <div className="w-9 h-9 mb-2.5 rounded-full bg-gradient-to-br from-[var(--premium-cta-start)] to-[var(--premium-cta-end)] flex items-center justify-center">
                      <TargetIcon size={18} className="text-[var(--premium-cta-text)]" />
                    </div>
                    <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-1 leading-tight">
                      {t("transcript.draftCard.title")}
                    </h3>
                    <p className="text-[13px] text-[var(--text-secondary)] leading-snug">
                      {t("transcript.draftCard.body")}
                    </p>
                  </button>
                )}

                {/* Summary card */}
                <button
                  data-action-id="transcript.ai.summary"
                  onClick={() => {
                    logClientEvent("featureDiscovery.selected", {
                      feature: "summary",
                      surface: "transcript-ai-tools",
                    });
                    router.push({
                      pathname: "/summary",
                      search: `?${new URLSearchParams({ id: transcriptId })}`,
                    });
                  }}
                  className="glass-tile glass-raise shrink-0 w-[200px] min-h-[135px] p-4 rounded-3xl flex flex-col items-start text-start active:scale-[0.98] active:bg-[var(--glass-bg-active)] transition-all"
                >
                  <div className="w-9 h-9 mb-2.5 rounded-full bg-[var(--accent-bg)] flex items-center justify-center">
                    <FileTextIcon size={18} className="text-[var(--accent)]" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-1 leading-tight">
                    {t("transcript.aiTools.summary.title")}
                  </h3>
                  <p className="text-[13px] text-[var(--text-secondary)] leading-snug">
                    {transcript?.summary
                      ? t("transcript.aiTools.summary.bodyReady")
                      : t("transcript.aiTools.summary.body")}
                  </p>
                </button>

                {/* Translate card */}
                <button
                  data-action-id="transcript.translate.open"
                  onClick={() => {
                    logClientEvent("featureDiscovery.selected", {
                      feature: "translate",
                      surface: "transcript-ai-tools",
                    });
                    useUISlice.getState().setAlertLanguagePicker({
                      onSelect: (languageCode) => {
                        router.push({
                          pathname: "/translate",
                          search: `?${new URLSearchParams({
                            id: transcriptId,
                            lang: languageCode,
                          })}`,
                        });
                      },
                    });
                  }}
                  className="glass-tile glass-raise shrink-0 w-[200px] min-h-[135px] p-4 rounded-3xl flex flex-col items-start text-start active:scale-[0.98] active:bg-[var(--glass-bg-active)] transition-all"
                >
                  <div className="w-9 h-9 mb-2.5 rounded-full bg-[var(--accent-bg)] flex items-center justify-center">
                    <TranslateIcon size={18} className="text-[var(--accent)]" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-1 leading-tight">
                    {t("transcript.aiTools.translate.title")}
                  </h3>
                  <p className="text-[13px] text-[var(--text-secondary)] leading-snug">
                    {t("transcript.aiTools.translate.body")}
                  </p>
                </button>

                {/* Quiz card */}
                <button
                  data-action-id="transcript.ai.quiz"
                  onClick={() => {
                    logClientEvent("featureDiscovery.selected", {
                      feature: "quiz",
                      surface: "transcript-ai-tools",
                    });
                    router.push({
                      pathname: "/quiz",
                      search: `?${new URLSearchParams({ id: transcriptId })}`,
                    });
                  }}
                  className="glass-tile glass-raise shrink-0 w-[200px] min-h-[135px] p-4 rounded-3xl flex flex-col items-start text-start active:scale-[0.98] active:bg-[var(--glass-bg-active)] transition-all"
                >
                  <div className="w-9 h-9 mb-2.5 rounded-full bg-[var(--accent-bg)] flex items-center justify-center">
                    <HelpCircleIcon
                      size={18}
                      className="text-[var(--accent)]"
                    />
                  </div>
                  <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-1 leading-tight">
                    {t("transcript.aiTools.quiz.title")}
                  </h3>
                  <p className="text-[13px] text-[var(--text-secondary)] leading-snug">
                    {t("transcript.aiTools.quiz.body")}
                  </p>
                </button>

                {/* Flashcards card */}
                <button
                  data-action-id="transcript.ai.flashcard"
                  onClick={() => {
                    logClientEvent("featureDiscovery.selected", {
                      feature: "flashcard",
                      surface: "transcript-ai-tools",
                    });
                    router.push({
                      pathname: "/flashcard",
                      search: `?${new URLSearchParams({ id: transcriptId })}`,
                    });
                  }}
                  className="glass-tile glass-raise shrink-0 w-[200px] min-h-[135px] p-4 rounded-3xl flex flex-col items-start text-start active:scale-[0.98] active:bg-[var(--glass-bg-active)] transition-all"
                >
                  <div className="w-9 h-9 mb-2.5 rounded-full bg-[var(--accent-bg)] flex items-center justify-center">
                    <CardIcon size={18} className="text-[var(--accent)]" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-1 leading-tight">
                    {t("transcript.aiTools.flashcard.title")}
                  </h3>
                  <p className="text-[13px] text-[var(--text-secondary)] leading-snug">
                    {t("transcript.aiTools.flashcard.body")}
                  </p>
                </button>

                {/* Search card */}
                <button
                  data-action-id="transcript.search.open"
                  onClick={() => {
                    logClientEvent("featureDiscovery.selected", {
                      feature: "search",
                      surface: "transcript-ai-tools",
                    });
                    useUISlice.getState().setAlertWidgetSearch({
                      transcriptId,
                      placeholder: t("alertWidgetSearch.placeholderTranscript"),
                    });
                  }}
                  className="glass-tile glass-raise shrink-0 w-[200px] min-h-[135px] p-4 rounded-3xl flex flex-col items-start text-start active:scale-[0.98] active:bg-[var(--glass-bg-active)] transition-all"
                >
                  <div className="w-9 h-9 mb-2.5 rounded-full bg-[var(--accent-bg)] flex items-center justify-center">
                    <SearchIcon size={18} className="text-[var(--accent)]" />
                  </div>
                  <h3 className="text-[15px] font-semibold text-[var(--text-primary)] mb-1 leading-tight">
                    {t("transcript.aiTools.search.title")}
                  </h3>
                  <p className="text-[13px] text-[var(--text-secondary)] leading-snug">
                    {t("transcript.aiTools.search.body")}
                  </p>
                </button>
              </div>
            </div>
          )}

          {/* Pending State */}
          {transcript && isPending && (
            <PendingState
              statusDetail={transcript.statusDetail}
              uploadEntry={uploadEntry}
            />
          )}

          {transcript?.recordingRecoveredAt && (
            <p className="mb-4 text-sm text-[var(--text-secondary)]" role="status">
              {t("transcript.recordingRecovery.partial", { defaultValue: "Recovered from uploaded audio. This recording may be incomplete." })}
            </p>
          )}

          {/* Failed State */}
          {transcript && isFailed && !hasSegments && transcriptId && (
            <FailedState
              transcriptId={transcriptId}
              transcriptionError={transcript.transcriptionError}
            />
          )}

          {/* Transcript Segments */}
          {hasSegments && !isPending && (
            <div className="pb-4">
              {transcript!.segments
                .slice(0, visibleSegmentsCount)
                .map((segment, idx) => (
                  <SegmentRow
                    key={`${segment.start}-${idx}`}
                    segment={segment}
                    transcriptId={transcriptId!}
                    hasSpeakers={hasSpeakers}
                    isActive={idx === activeSegmentIndex}
                    isPlaying={audioIsPlaying}
                    isAudioFile={isAudioFile}
                    hasAudio={hasAudio}
                    onPlaySegment={playGuestAudio}
                    onPauseAudio={handlePauseAudio}
                    highlightTerm={searchQuery}
                    direction={getTranscriptDirection(transcript!)}
                  />
                ))}
              {visibleSegmentsCount < transcript!.segments.length && (
                <button
                  data-action-id="transcript.segments.loadMore"
                  onClick={() => setVisibleSegmentsCount((prev) => prev + 50)}
                  className="btn btn-glass w-full text-[15px] flex items-center justify-center mt-6 mb-8 active:scale-[0.97] active:bg-[var(--glass-bg-active)] transition-all"
                >
                  {t("transcript.loadMore")}
                </button>
              )}
            </div>
          )}

          {/* What the preview left out: placeholder bars, never real or
              invented text, so the gap is felt before the offer below. */}
          {showSubscriptionOffer &&
            showPartialTranscriptUpsell &&
            visibleSegmentsCount >= transcript!.segments.length && (
              <div
                data-testid="transcript-locked-preview"
                className="relative mb-8 px-1"
              >
                <div aria-hidden="true" className="space-y-3 blur-[3px]">
                  {[94, 81, 88, 67, 90].map((width) => (
                    <div
                      key={width}
                      className="h-3.5 rounded-full bg-[var(--fill-3)]"
                      style={{ width: `${width}%` }}
                    />
                  ))}
                </div>
                <div className="absolute inset-0 flex items-center justify-center">
                  <span className="glass flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-[var(--text-primary)]">
                    <LockIcon size={14} />
                    {remainingMinutes
                      ? t("transcript.lockedMinutes", {
                          minutes: remainingMinutes,
                        })
                      : t("transcript.lockedRest")}
                  </span>
                </div>
              </div>
            )}

          {/* Paywall Footer */}
          {showSubscriptionOffer && (
            <div ref={guestOfferRef} className="mb-0 -mx-5">
              <AlertWidgetPaywall.Footer
                completeTranscript={showShortTranscriptUpsell}
                fileName={fileTitle}
                remainingMinutes={remainingMinutes}
                source={
                  uploadEntry
                    ? "uploadEntry"
                    : isWebsiteHandoff
                      ? "guestTranscript"
                      : undefined
                }
              />
            </div>
          )}

          {/* Transcribe Full Audio Button */}
          {showTranscribeFullAudioButton && (
            <div className="mb-8 px-1">
              <button
                data-action-id="transcript.fullAudio"
                onClick={() => {
                  if (transcriptId) {
                    void actions.transcript.transcribeFullAudio(transcriptId);
                  }
                }}
                className="btn btn-primary w-full text-[15px] active:scale-[0.97] transition-all"
              >
                <SparklesIcon size={18} className="mr-2" />
                {t(
                  isPremiumRedo
                    ? "transcript.redoWithPremium"
                    : "transcript.transcribeFullAudio",
                )}
              </button>
            </div>
          )}

          {transcript && (
            <TranscriptRatingWidget
              transcriptId={transcriptId}
              transcript={transcript}
              offerAccuracy={!isSubscribed && isFreeDraft(transcript)}
            />
          )}

          {/* Bottom spacer for action bar */}
          <div className="h-44" />
        </div>
      </main>

      {/* Upload entry has no bottom bar, so its notice floats. */}
      {uploadEntry && offerNotice}

      {/* Scroll to Top Button */}
      <button
        onClick={scrollToTop}
        data-action-id="transcript.scrollTop"
        style={
          partialNoticeHeight
            ? {
                bottom: `calc(env(safe-area-inset-bottom, 0px) + ${partialNoticeBottom} + ${partialNoticeHeight}px + 0.75rem)`,
              }
            : {
                bottom: `calc(env(safe-area-inset-bottom, 0px) + ${partialNoticeBottom})`,
              }
        }
        className={`glass fixed end-5 z-20 w-11 h-11 rounded-full flex items-center justify-center text-[var(--text-primary)] transition-all duration-300 ${
          showScrollTop
            ? "opacity-100 translate-y-0"
            : "opacity-0 translate-y-10 pointer-events-none"
        } hover:bg-[var(--glass-bg-hover)] active:scale-90`}
      >
        <ChevronUpIcon size={24} />
      </button>

      {/* ─── Bottom Action Bar ─── */}
      {transcript && hasSegments && !isPending && !uploadEntry && (
        <>
          <DropdownMenu
            open={showBottomMenu}
            onClose={() => setShowBottomMenu(false)}
            position="bottom"
          >
            <MenuItem
              actionId="transcript.ai.summary"
              icon={<FileTextIcon size={16} />}
              label={t("transcript.aiPanel.summary")}
              onClick={() => {
                setShowBottomMenu(false);
                router.push({
                  pathname: "/summary",
                  search: `?${new URLSearchParams({ id: transcriptId })}`,
                });
              }}
            />
            <MenuItem
              actionId="transcript.translate.open"
              icon={<TranslateIcon size={16} />}
              label={t("transcript.aiPanel.translateTo")}
              onClick={() => {
                setShowBottomMenu(false);
                openTranslation();
              }}
            />
            <MenuItem
              actionId="transcript.ai.quiz"
              icon={<HelpCircleIcon size={16} />}
              label={t("transcript.aiPanel.quiz")}
              onClick={() => {
                setShowBottomMenu(false);
                router.push({
                  pathname: "/quiz",
                  search: `?${new URLSearchParams({ id: transcriptId })}`,
                });
              }}
            />
            <MenuItem
              actionId="transcript.ai.flashcard"
              icon={<CardIcon size={16} />}
              label={t("transcript.aiPanel.flashcards")}
              onClick={() => {
                setShowBottomMenu(false);
                router.push({
                  pathname: "/flashcard",
                  search: `?${new URLSearchParams({ id: transcriptId })}`,
                });
              }}
            />
          </DropdownMenu>

          {/* The notice adds its own height, so the fade above it shrinks. */}
          <BottomActionBar
            compactBottom
            rootClassName={offerNotice ? "!pt-4" : undefined}
          >
            {offerNotice}
            {/* Main Action Buttons */}
            <div
              data-testid="transcript-bottom-actions"
              className="mx-auto flex w-fit max-w-full items-center justify-center gap-2.5"
            >
              {/* Copy - Primary */}
              <button
                data-action-id="transcript.copy"
                onClick={copyVisibleTranscript}
                className="btn btn-primary shrink-0 gap-2 px-5 text-[14px] active:scale-[0.97] transition-all"
              >
                <CopyIcon size={18} />
                {t("transcript.copy")}
              </button>

              {/* Export */}
              <button
                data-action-id="transcript.export"
                onClick={exportVisibleTranscript}
                className="btn btn-glass shrink-0 gap-2 px-5 text-[14px] active:scale-[0.97] transition-all"
              >
                <DownloadIcon size={18} />
                {t("transcript.download")}
              </button>

              {/* More */}
              <button
                onClick={() => setShowBottomMenu(!showBottomMenu)}
                data-action-id="transcript.ai.menu.toggle"
                className={`h-14 w-14 rounded-full flex items-center justify-center active:scale-[0.97] transition-all ${
                  showBottomMenu
                    ? "bg-[var(--accent)] text-[var(--text-inverse)] shadow-[0_18px_34px_-18px_var(--accent-glow)]"
                    : "glass text-[var(--text-primary)]"
                }`}
              >
                <MoreHorizontalIcon size={20} />
              </button>
            </div>
          </BottomActionBar>
        </>
      )}
      <TranscriptNativeCapabilities
        transcriptId={transcript?.id}
        transcriptStatus={transcript?.status}
        navigateHome={navigateHome}
        showBottomMenu={showBottomMenu}
        setShowBottomMenu={setShowBottomMenu}
        showMore={showMore}
        setShowMore={setShowMore}
      />
    </Container>
  );
}
